import { useEffect, type MutableRefObject } from 'react';
import { validateTradeEntry, type TradeEntryFormData } from '@/services/trades/trade-service';
import type { TradeForm } from '../../components/trade-form-sections/types';
import { CUSTODIAN_ACCOUNT_UNRESOLVED_MESSAGE } from './custodian-account';
import type { TradeFormValues, SearchInventoryItem } from '../useCreateTradeForm';
import { poolsItsCash } from '@/utils/trades/cash-pool-rate';
import type { Counterparty } from '@/types/counterparty.types';
import { missingAgreementMessage, noRepoCallRateMessage } from './counterparty-agreement';

type ValidationError = { field: string; message: string; severity: 'error' | 'warning' };

/**
 * Debounced (300ms) cross-field validation. Reads the live form values, runs
 * validateTradeEntry, appends the "Trade reason is required" rule, and publishes
 * the result. The dependency array is kept verbatim from useCreateTradeForm
 * (LR-495).
 *
 * LR-612: a dep is only HALF the wiring. Reading a value off `form.state`
 * subscribes to nothing, so the array is re-evaluated only when the host
 * re-renders - which needs a `useStore` on the same key in useCreateTradeForm
 * or in a hook it calls, because a `form.Field` in a child re-renders that
 * child alone. LR-586 shipped the cash_pool dep without the subscription and
 * the summary went stale until some OTHER field moved. Both halves are
 * asserted, for all three dep shapes, in
 * __tests__/trade-form-validation-deps.test.ts.
 */
export function useTradeFormValidation({
  form,
  collateralType,
  dividendPassthroughPercent,
  tradeReason,
  selectedInventoryItem,
  setValidationErrors,
  pendingValidation,
  counterparties,
  repoCallRateMissing,
}: {
  form: TradeForm;
  collateralType: TradeFormValues['collateral_type'];
  dividendPassthroughPercent: TradeFormValues['dividend_passthrough_percent'];
  tradeReason: TradeFormValues['trade_reason'];
  selectedInventoryItem: SearchInventoryItem | null;
  setValidationErrors: (errors: ValidationError[]) => void;
  /** The scheduled check, so a submit can cancel it. */
  pendingValidation: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  counterparties: Counterparty[];
  /** A repo whose security currency has no COB rate into its GMRA agreement currency today. */
  repoCallRateMissing: { from: string; to: string } | null;
}) {
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const formDataForValidation: Partial<TradeEntryFormData> = {
        position: {
          tradeType: form.state.values.trade_type,
          collateralType: collateralType,
          quantity: form.state.values.quantity,
          availableQuantity: selectedInventoryItem?.quantity,
          contractPrice: typeof form.state.values.gross_price === 'string' ? parseFloat(form.state.values.gross_price) || 0 : form.state.values.gross_price,
          marginBps: form.state.values.margin_bps || 0,
        },
        security: {
          isin: selectedInventoryItem?.isin,
          currency: selectedInventoryItem?.currency,
        },
        rate: {
          rateEntryMode: form.state.values.rate_entry_mode,
          rateType: form.state.values.rate_type,
          fixedRate: typeof form.state.values.benchmark_rate === 'string' ? parseFloat(form.state.values.benchmark_rate) : form.state.values.benchmark_rate,
          spreadBps: form.state.values.spread_bps ?? undefined,
          benchmark: form.state.values.benchmark_name,
          dividendPassThrough: typeof dividendPassthroughPercent === 'string' ? parseFloat(dividendPassthroughPercent) : dividendPassthroughPercent,
          feePercent: typeof form.state.values.fee_percent === 'string' ? parseFloat(form.state.values.fee_percent) : form.state.values.fee_percent ?? undefined,
        },
        counterparty: {
          name: form.state.values.counterparty_name,
        },
        dates: {
          tradeDate: form.state.values.trade_date,
          settlementDate: form.state.values.settlement_date,
          tradeDuration: form.state.values.trade_duration as 'OPEN' | 'EVERGREEN' | 'TERM' | undefined,
          maturityDate: form.state.values.maturity_date || undefined,
          noticePeriodDays: form.state.values.notice_period_days ?? undefined,
        },
        settlement: {
          deliveryType: form.state.values.delivery_type,
          venue: form.state.values.settlement_venue,
          settlementCurrency: form.state.values.settlement_currency,
          fxRate: form.state.values.fx_rate,
          netSettlementConvertedInput: form.state.values.net_settlement_converted_input,
        },
      };
      const errors = validateTradeEntry(formDataForValidation);

      // Validation for trade reason and comment
      if (!tradeReason) {
        errors.push({ field: 'trade_reason', message: 'Trade reason is required', severity: 'error' });
      }

      // LR-586: cash collateral must name the pool that holds it. Lives here
      // rather than in validateTradeEntry because the pool is a create-form field,
      // absent from the shared TradeEntryFormData shape.
      // LR-631: and only in the modes that pool it. Without `poolsItsCash` the
      // DEFAULT booking - GMSLA lend, cash, rebate - raises a blocking error
      // naming a field the form no longer renders and the rebate guard nulls on
      // sight, so it can never be cleared. The same predicate gates the field,
      // the registry query and the submit payload.
      if (
        collateralType === 'cash'
        && poolsItsCash(form.state.values.trade_type, form.state.values.rate_entry_mode)
        && !form.state.values.cash_pool
      ) {
        errors.push({
          field: 'cash_pool',
          message: 'Cash pool is required for cash collateral',
          severity: 'error',
        });
      }

      // Shown before Submit rather than as the booking's 400.
      const agreementRefusal = missingAgreementMessage(
        counterparties, form.state.values.counterparty_name, form.state.values.trade_type,
      );
      if (agreementRefusal) {
        errors.push({ field: 'counterparty_name', message: agreementRefusal, severity: 'error' });
      }
      if (repoCallRateMissing) {
        errors.push({
          field: 'currency',
          message: noRepoCallRateMessage(repoCallRateMissing.from, repoCallRateMissing.to),
          severity: 'warning',
        });
      }

      // LR-625: same rule as the onSubmit guard, published here so the summary
      // panel carries it too. Needed because the Custodian field validator
      // aborts handleSubmit before onSubmit runs, so the guard alone would
      // never reach the panel when no custodian is picked — every other
      // required field appears in both places (validateTradeEntry above).
      if (!form.state.values.custodian_account) {
        errors.push({
          field: 'custodian_account',
          message: CUSTODIAN_ACCOUNT_UNRESOLVED_MESSAGE,
          severity: 'error',
        });
      }

      setValidationErrors(errors);
    }, 300);
    pendingValidation.current = timeoutId;
    return () => clearTimeout(timeoutId);
    // setValidationErrors is a stable setState setter; omitted to keep the
    // original dependency array verbatim.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.state.values.trade_type,
    collateralType,
    form.state.values.quantity,
    form.state.values.gross_price,
    form.state.values.margin_bps,
    form.state.values.rate_entry_mode,
    form.state.values.rate_type,
    form.state.values.benchmark_rate,
    form.state.values.spread_bps,
    form.state.values.benchmark_name,
    form.state.values.fee_percent,
    dividendPassthroughPercent,
    form.state.values.counterparty_name,
    form.state.values.trade_date,
    form.state.values.settlement_date,
    form.state.values.trade_duration,
    form.state.values.maturity_date,
    form.state.values.notice_period_days,
    form.state.values.delivery_type,
    form.state.values.settlement_venue,
    form.state.values.settlement_currency,
    form.state.values.fx_rate,
    form.state.values.net_settlement_converted_input,
    tradeReason,
    form.state.values.cash_pool,
    form.state.values.custodian_account,
    selectedInventoryItem?.quantity,
    selectedInventoryItem?.isin,
    selectedInventoryItem?.currency,
    counterparties,
    repoCallRateMissing,
  ]);
}
